import { getCustomProductTiles } from '@features/products/api/get-custom-product-grid-tiles'

import { getCurrencyFromCountry } from '@lib/utils'
import { PayloadAction, createSelector } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { createAppSlice } from './createAppSlice'
import { AppThunk } from './store'

export interface WishlistState {
  skus: string[]
}

const WISHLIST_STORAGE_KEY = 'wishlist'

// Utility to load wishlist from localStorage or cookies
export const loadWishlist = (): string[] => {
  try {
    // Check if data exists in Cookies or localStorage
    const data = localStorage.getItem(WISHLIST_STORAGE_KEY)

    // Parse and return if data exists
    if (data) {
      return JSON.parse(data)
    }
  } catch (error) {
    console.error('Failed to load wishlist from storage:', error)
  }

  // Return empty array if no valid data found
  return []
}

// Utility to save wishlist to localStorage and cookies
const saveWishlist = (wishlist: string[]) => {
  const data = JSON.stringify(wishlist)
  // Cookies.set(WISHLIST_STORAGE_KEY, data, { path: '/', expires: 30 })
  localStorage.setItem(WISHLIST_STORAGE_KEY, data)
}

const initialWishlistState: WishlistState = {
  skus: [],
}

export const wishlistSlice = createAppSlice({
  name: 'wishlist',
  initialState: initialWishlistState,
  reducers: {
    setInitialWishlist(state, { payload }: PayloadAction<string[]>) {
      state.skus = payload
    },
    addItem(state, { payload }: PayloadAction<string>) {
      const existingItem = state.skus.find((item) => item === payload)
      if (!existingItem) {
        state.skus.push(payload)
      }
      saveWishlist(state.skus)
    },
    removeItem(state, { payload }: PayloadAction<string>) {
      state.skus = state.skus.filter((item) => item !== payload)
      saveWishlist(state.skus)
    },
    clearWishlist(state) {
      state.skus = []
      saveWishlist(state.skus)
    },
  },
  selectors: {
    selectWishlist: (state: WishlistState) => state.skus,
    selectWishlistCount: (state: WishlistState) => state.skus.length,
  },
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action) => {
      return {
        ...state,
        //@ts-ignore
        ...action.payload.wishlist,
      }
    })
  },
})

export const fetchWishlistProducts =
  (): AppThunk => async (dispatch, getState) => {
    const country = getState().global.data?.siteSettings.country?.code ?? 'DK'
    const channel = getState().global.data.channel
    const currency = getCurrencyFromCountry(country)
    const state = getState().wishlist
    const ids = state.skus

    if (ids.length === 0) return []

    const query = ids.map((id) => `variant_id:${id}`).join(' OR ')

    const gridProducts = await getCustomProductTiles({
      channel,
      currency,
      query,
    })

    return gridProducts
  }

// Factory selector to check if an item is in the wishlist
export const selectAlreadyInWishList = (id: string) =>
  createSelector([wishlistSlice.selectors.selectWishlist], (ids) =>
    ids.includes(id),
  )

export const { addItem, removeItem, clearWishlist, setInitialWishlist } =
  wishlistSlice.actions

// Export selectors
export const { selectWishlist, selectWishlistCount } = wishlistSlice.selectors

export default wishlistSlice
